import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TransitionGroup } from 'react-transition-group';
import { Typography, Fade, makeStyles } from '@material-ui/core';
import SectionTitle from '../sectionTitle';
import DotsMenu from '../dotsMenu';
import StatusIndicator from '../statusIndicator';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import Tag from '../tag';
import UserCount from '../userCount';
import UserLink from '../userLink';
import TasksList from '../tasksList';
import TextLink from '../textLink';
import RequestedIconTooltip from '../requestedIconTooltip';
import ProposedStatus from '../proposedStatus';
import UpdatesInfo from '../updatesInfo';
import { ReactComponent as PreviewUserIcon } from '../../../assets/icons/remove_red_eye.svg';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { ACTION_PLAN_FIELDS } from '../../../constants/actionPlan';
import { api_action_plans_with_view_permission } from '../../../constants/apiRoutes';
import { prepareNotificationUpdates } from '../../../utility/globalNotifications';
import { formatDate } from '../../../utility/dateUtils';
import { isArrayEmpty } from '../../../utility/helpers';
import { hasAuthPermissionCheck } from '../../../utility/permissions';
import { prepareActionPlansDotsMenuItems } from '../../../utility/actionPlans';
import http from '../../../utility/http';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  heading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 30,
    marginBottom: spacing(4),
  },
  root: {
    boxSizing: 'border-box',
    backgroundColor: primary.bluish9,
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1fr) 230px`,
    width: '100%',
    borderRadius: 4,
    marginBottom: spacing(7),
  },
  content: {
    boxSizing: 'border-box',
    padding: spacing(6),
    width: '100%',
  },
  info: {
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(6, 6, 6, 4),
    borderLeft: `1px dashed ${primary.bluish7}`,
  },
  sectionWrapper: {
    marginBottom: spacing(4),
  },
  label: {
    color: primary.bluish3,
  },
  labelSpacing: {
    marginBottom: spacing(1.5),
  },
  day: {
    marginBottom: spacing(1),
  },
  dotsMenu: {
    position: 'absolute',
    top: 24,
    right: 24,
    height: 20,
    width: 'auto',
  },
  userCount: {
    position: 'absolute',
    bottom: 21,
    right: 22,
  },
  tagsSection: {
    marginBottom: spacing(2),
  },
  tagsWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
  },
  tagsWrapperWithoutTags: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 28,
  },
  tagWrapper: {
    margin: spacing(0, 2, 2, 0),
  },
  coursesWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  courseWrapper: {
    display: 'flex',
    marginBottom: spacing(2),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  createdByHighlightedLabel: {
    fontFamily: 'ProximaNova-Bold',
  },
  autoSpacing: {
    marginTop: 'auto',
    maxWidth: 'calc(100% - 26px)',
  },
  userWrapper: {
    display: 'flex',
    marginTop: spacing(1.5),
  },
  user: {
    maxWidth: 'calc(100% - 30px)',
  },
  proposed: {
    marginTop: spacing(5.5),
    marginLeft: spacing(1.5),
  },
}));

const DAY_MONTH_FORMAT = 'MMM d';
const YEAR_FORMAT = 'yyyy';

const ActionPlanDetailsCard = ({
  translations,
  actionPlan,
  statuses,
  auth,
  accessControl,
  notifications = null,
  isManagable,
  isDeletable,
  isDisabled = false,
  isPendingProposal,
  canManageOwnPendingProposal,
  hasCoursesFeatureEnabled,
  onManageTasks = () => {},
  onDelete = () => {},
  onEdit = () => {},
  onDismissProposal,
  onTaskStatusChange = () => {},
  canManageTaskCompletionCheck,
  canAccessUserProfileCheck,
  goToUserProfilePage,
  onClearNotifications = () => {},
}) => {
  const classes = useStyles();

  const {
    title,
    creator,
    due_date,
    user,
    created_on,
    tags,
    progress,
    tasks,
    courses,
    is_proposed,
    is_pending_proposal,
  } = actionPlan;
  const isCreatedForAccessible = canAccessUserProfileCheck(user.id);
  const isCreatorAccessible =
    !!creator?.id && canAccessUserProfileCheck(creator.id);
  const isCreatedByMe = auth.id === creator?.id;
  const canViewCourses = hasAuthPermissionCheck(
    PERMISSION_CODES.LEARNING_VIEW,
    accessControl
  );
  const hasCourses = !isArrayEmpty(courses);
  const isCreatedForCurrentUser = user.id === auth.id;
  const canManage = isManagable || canManageOwnPendingProposal;
  const canDelete = isDeletable || canManageOwnPendingProposal;
  const notificationsData = useMemo(() => {
    if (notifications) {
      return prepareNotificationUpdates(
        translations.notifications.updates,
        notifications.model,
        notifications.updates
      );
    }

    return null;
  }, [translations, notifications]);
  const hasNotifications = !!notificationsData;

  const handleTaskStatusChange = useCallback(
    task => {
      onTaskStatusChange(task, isManagable);
    },
    [isManagable, onTaskStatusChange]
  );

  const getUsersWithViewPermission = useCallback(
    () => http.get(api_action_plans_with_view_permission(actionPlan.id)),
    [actionPlan.id]
  );

  const renderStatusLabel = () => {
    const currentStatus = statuses.find(
      status => status.value === actionPlan[ACTION_PLAN_FIELDS.STATUS]
    );

    return <StatusIndicator status={currentStatus} />;
  };

  return (
    <>
      <div className={classes.heading}>
        <SectionTitle title={translations.details} />
        <UpdatesInfo
          translations={translations.notifications}
          updates={notificationsData}
          isUpdate={notifications?.isUpdate}
          hasUpdates={hasNotifications}
          onClearAll={onClearNotifications}
        />
      </div>
      <div className={classes.root}>
        <div className={classes.content}>
          <div
            className={classNames(classes.userWrapper, classes.sectionWrapper)}
          >
            <UserLink
              className={classNames({ [classes.user]: is_proposed })}
              labelClass={classes.label}
              label={translations.createdFor}
              user={user}
              isUserAccessible={isCreatedForAccessible}
            />
            {is_proposed && (
              <RequestedIconTooltip
                className={classes.proposed}
                labels={translations.proposedTooltip}
                isRequestedByCurrentUser={isCreatedForCurrentUser}
              />
            )}
          </div>
          <div className={classes.sectionWrapper}>
            <Typography
              className={classNames(classes.label, classes.labelSpacing)}
              variant="body2"
            >
              {translations.title}
            </Typography>
            <TextBoxWithTooltip variant="subtitle2" text={title} />
          </div>
          <div
            className={classNames(classes.sectionWrapper, classes.tagsSection)}
          >
            <Typography
              className={classNames(classes.label, classes.labelSpacing)}
              variant="body2"
            >
              {translations.tags}
            </Typography>
            <TransitionGroup className={classes.tagsWrapper}>
              {tags.map(tag => (
                <Fade key={`tag_item_${tag.id}`} in unmountOnExit>
                  <div className={classes.tagWrapper}>
                    <Tag tag={tag} color={tag.category.color} isSelected />
                  </div>
                </Fade>
              ))}
            </TransitionGroup>
            <Fade in={isArrayEmpty(tags)} unmountOnExit>
              <Typography className={classes.tagsWrapperWithoutTags}>
                -
              </Typography>
            </Fade>
          </div>
          <div className={classes.sectionWrapper}>
            <Typography
              className={classNames(classes.label, classes.labelSpacing)}
              variant="body2"
            >
              {translations.tasks}
            </Typography>
            <TasksList
              translations={translations.tasksList}
              tasks={tasks}
              checkCanManageTask={canManageTaskCompletionCheck}
              canAccessUserProfile={canAccessUserProfileCheck}
              onStatusChange={handleTaskStatusChange}
            />
            <Fade in={isArrayEmpty(tasks)} unmountOnExit>
              <Typography variant="body2">-</Typography>
            </Fade>
          </div>
          {hasCoursesFeatureEnabled && canViewCourses && (
            <div>
              <Typography
                className={classNames(classes.label, classes.labelSpacing)}
                variant="body2"
              >
                {translations.courses}
              </Typography>
              <Fade in={!hasCourses} unmountOnExit>
                <Typography variant="body2">-</Typography>
              </Fade>
              <TransitionGroup className={classes.coursesWrapper}>
                {courses.map(course => (
                  <Fade
                    key={`course_item_${course.id}`}
                    className={classes.courseWrapper}
                    in
                    unmountOnExit
                  >
                    <div>
                      <TextLink
                        to={`/learning/${course.id}`}
                        variant="subtitle1"
                        text={course.title}
                      />
                    </div>
                  </Fade>
                ))}
              </TransitionGroup>
            </div>
          )}
        </div>
        <div className={classes.info}>
          <div className={classes.sectionWrapper}>
            {isPendingProposal ? (
              <ProposedStatus proposedLabel={translations.proposed} />
            ) : (
              renderStatusLabel()
            )}
          </div>
          {!isPendingProposal && (
            <div className={classes.sectionWrapper}>
              <Typography className={classes.labelSpacing} variant="body2">
                {translations.completeness}
              </Typography>
              <Typography variant="subtitle1">{`${progress}%`}</Typography>
            </div>
          )}
          <div className={classes.sectionWrapper}>
            <Typography className={classes.labelSpacing} variant="body2">
              {translations.dueDate}
            </Typography>
            <Typography variant="subtitle1" className={classes.day}>
              {formatDate(due_date, DAY_MONTH_FORMAT)}
            </Typography>
            <Typography variant="overline">
              {formatDate(due_date, YEAR_FORMAT)}
            </Typography>
          </div>
          <div className={classes.sectionWrapper}>
            <Typography className={classes.labelSpacing} variant="body2">
              {isPendingProposal
                ? translations.proposedOn
                : translations.createdOn}
            </Typography>
            <Typography variant="subtitle1" className={classes.day}>
              {formatDate(created_on, DAY_MONTH_FORMAT)}
            </Typography>
            <Typography variant="overline">
              {formatDate(created_on, YEAR_FORMAT)}
            </Typography>
          </div>
          {!isPendingProposal && (
            <UserLink
              className={classes.autoSpacing}
              label={translations.createdBy}
              labelClass={classNames({
                [classes.createdByHighlightedLabel]: isCreatedByMe,
              })}
              meLabel={translations.createdByMe}
              user={creator}
              isUserAccessible={isCreatorAccessible}
              isCurrentUser={isCreatedByMe}
            />
          )}
          <UserCount
            className={classes.userCount}
            icon={PreviewUserIcon}
            tooltipTitle={translations.usersListTitle}
            tooltipLabel={translations?.usersListTooltip}
            hasUserCount={false}
            canAccessUserProfileCheck={canAccessUserProfileCheck}
            getUsers={getUsersWithViewPermission}
            goToUserProfile={goToUserProfilePage}
            isPreviewIcon
          />
          {(canManage || canDelete || isCreatedForCurrentUser) && (
            <DotsMenu
              className={classes.dotsMenu}
              menuItems={prepareActionPlansDotsMenuItems(
                translations.menuItemLabels,
                actionPlan,
                canManage,
                canDelete,
                isDisabled,
                is_pending_proposal,
                isCreatedForCurrentUser,
                onEdit,
                onDelete,
                onManageTasks,
                onDismissProposal
              )}
            />
          )}
        </div>
      </div>
    </>
  );
};

ActionPlanDetailsCard.propTypes = {
  translations: PropTypes.object.isRequired,
  actionPlan: PropTypes.shape({}).isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  notifications: PropTypes.object,
  isManagable: PropTypes.bool.isRequired,
  isDeletable: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
  isPendingProposal: PropTypes.bool.isRequired,
  canManageOwnPendingProposal: PropTypes.bool.isRequired,
  hasCoursesFeatureEnabled: PropTypes.bool.isRequired,
  canManageTaskCompletionCheck: PropTypes.func.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onDismissProposal: PropTypes.func.isRequired,
  onManageTasks: PropTypes.func,
  onDelete: PropTypes.func,
  goToUserProfilePage: PropTypes.func.isRequired,
  onTaskStatusChange: PropTypes.func,
  onClearNotifications: PropTypes.func,
};

export default ActionPlanDetailsCard;
